import { useState, useCallback, useEffect } from 'react';

function storageKey(workspaceId: string | null | undefined, userId: string) {
  const ws = workspaceId ?? 'no_workspace';
  return `pp_tasks_catchball_hidden_v1_${ws}_${userId}`;
}

/**
 * Per-user hidden Catchball "ball in your court" rows on the Tasks page (localStorage only).
 * IDs: `turn:${turnId}` | `legacy:${loopId}`
 */
export function useCatchballQueueArchive(
  userId: string | null | undefined,
  workspaceId: string | null | undefined
) {
  const [hiddenIds, setHiddenIds] = useState<Set<string>>(() => new Set());

  useEffect(() => {
    if (!userId) {
      setHiddenIds(new Set());
      return;
    }
    try {
      const raw = localStorage.getItem(storageKey(workspaceId, userId));
      if (raw) {
        const arr = JSON.parse(raw) as string[];
        setHiddenIds(new Set(Array.isArray(arr) ? arr : []));
      } else {
        setHiddenIds(new Set());
      }
    } catch {
      setHiddenIds(new Set());
    }
  }, [userId, workspaceId]);

  const persist = useCallback(
    (next: Set<string>) => {
      if (!userId) return;
      try {
        if (next.size === 0) {
          localStorage.removeItem(storageKey(workspaceId, userId));
        } else {
          localStorage.setItem(storageKey(workspaceId, userId), JSON.stringify([...next]));
        }
      } catch {
        // ignore
      }
    },
    [userId, workspaceId]
  );

  const hideIds = useCallback(
    (ids: string[]) => {
      if (ids.length === 0) return;
      setHiddenIds(prev => {
        const next = new Set(prev);
        for (const id of ids) next.add(id);
        persist(next);
        return next;
      });
    },
    [persist]
  );

  const restoreIds = useCallback(
    (ids: string[]) => {
      if (ids.length === 0) return;
      setHiddenIds(prev => {
        const next = new Set(prev);
        for (const id of ids) next.delete(id);
        persist(next);
        return next;
      });
    },
    [persist]
  );

  return { hiddenCatchballIds: hiddenIds, hideCatchballIds: hideIds, restoreCatchballIds: restoreIds };
}

export { catchballLegacyRowId, catchballTurnRowId } from '../lib/catchballQueueRowIds';
